import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../../../../axios';
import { AppThunk } from '../../../../../store';
import { ToastAnt } from '../../../../Shared/Toast/ToastAnt';
import { setDataAfterImageQCAssessment, setDataAfterTurbineQCUncheckAssessment } from '../../annotation.slice';
import { CachedImage, SequenceInfo, SequenceStatusInfo } from '../../annotation.types';
import { getCurrentAndPossibleTurbineStatuses } from '../TurbineStatus/turbineStatus.slice';

export type DataQCAssessment = {
  haveQC: boolean;
  comment: string;
  isBlurred: boolean;
  hasContrastIssues: boolean;
};

export type CreateDataQCAssessment = {
  haveQC: boolean;
  comment: string;
  isBlurred: boolean;
  hasContrastIssues: boolean;
};

type State = {
  dataQCAssessment: DataQCAssessment | null;
  loadingDataQCAssessment: boolean;
  creatingDataQCAssessment: boolean;
};

const initialState: State = {
  dataQCAssessment: null,
  loadingDataQCAssessment: false,
  creatingDataQCAssessment: false
};

const qcSlice = createSlice({
  name: 'qc',
  initialState,
  reducers: {
    setDataQCAssessment(state, action: PayloadAction<DataQCAssessment | null>) {
      state.dataQCAssessment = action.payload;
    },
    setLoadingDataQCAssessment(state, action: PayloadAction<boolean>) {
      state.loadingDataQCAssessment = action.payload;
    },
    setCreatingDataQCAssessment(state, action: PayloadAction<boolean>) {
      state.creatingDataQCAssessment = action.payload;
    }
  }
});

export const { setDataQCAssessment, setLoadingDataQCAssessment, setCreatingDataQCAssessment } = qcSlice.actions;

export default qcSlice.reducer;

export const getTurbineDataQCAssessment =
  (turbinePath: string): AppThunk =>
  async dispatch => {
    try {
      dispatch(setLoadingDataQCAssessment(true));

      const url = `DataQCAssessment/turbine/${turbinePath}`;
      const response = await axios.get<DataQCAssessment | null>(url);
      dispatch(setDataQCAssessment(response.data));
    } catch (error) {
      ToastAnt('An error occured.');
    }

    dispatch(setLoadingDataQCAssessment(false));
  };

export const createTurbineDataQCAssessment =
  (turbinePath: string, haveQC: boolean, comment: string): AppThunk =>
  async dispatch => {
    try {
      dispatch(setCreatingDataQCAssessment(true));
      const isBlurred: boolean = false;
      const hasContrastIssues: boolean = false;
      const model: CreateDataQCAssessment = {
        haveQC,
        comment,
        isBlurred,
        hasContrastIssues
      };
      const url = `DataQCAssessment/turbine/${turbinePath}`;
      const response = await axios.post(url, model);
      dispatch(setDataQCAssessment(model));
      dispatch(setCreatingDataQCAssessment(false));
      dispatch(getCurrentAndPossibleTurbineStatuses(turbinePath));
      if (!haveQC) {
        dispatch(updateTurbineQCUncheckAssessment());
      }
      ToastAnt('Saved.');
    } catch (error) {
      ToastAnt('An error occured.');
    }
  };

export const createImageDataQCAssessment =
  (imagePath: string, haveQC: boolean, comment: string, isBlurred: boolean, hasContrastIssues: boolean): AppThunk =>
  async dispatch => {
    try {
      dispatch(setCreatingDataQCAssessment(true));
      const model: CreateDataQCAssessment = {
        haveQC,
        comment,
        isBlurred,
        hasContrastIssues
      };
      const url = `DataQCAssessment/image/${imagePath}`;
      const response = await axios.post(url, model);
      dispatch(updateSelectedImageQCAssessment(model));
      ToastAnt('Saved.');
    } catch (error) {
      ToastAnt('An error occured.');
    }
    dispatch(setCreatingDataQCAssessment(false));
  };

export const updateTurbineQCUncheckAssessment = (): AppThunk => async (dispatch, getState) => {
  const { selectedSequenceInfo, imagesCache, sequenceStatuses } = getState().annotation;

  if (!(selectedSequenceInfo && selectedSequenceInfo.images)) {
    return;
  }

  let updatedSelectedSequenceInfo: SequenceInfo = {
    ...selectedSequenceInfo,
    images: selectedSequenceInfo.images.map(x => {
      return {
        ...x,
        haveQC: false
      };
    })
  };

  let updatedImagesCache: CachedImage[] = imagesCache.map(x => {
    if (x.image.dataQCAssessment) {
      return {
        ...x,
        image: {
          ...x.image,
          dataQCAssessment: {
            ...x.image.dataQCAssessment,
            haveQC: false
          }
        }
      };
    }

    return x;
  });

  let updatedSequenceStatuses: SequenceStatusInfo[] = sequenceStatuses.map(x => {
    return {
      ...x,
      haveQC: false
    };
  });

  dispatch(
    setDataAfterTurbineQCUncheckAssessment({
      updatedSelectedSequenceInfo,
      updatedImagesCache,
      updatedSequenceStatuses
    })
  );
};

export const updateSelectedImageQCAssessment =
  (assessmentData: DataQCAssessment): AppThunk =>
  async (dispatch, getState) => {
    const { image, selectedSequenceInfo, imagesCache, sequenceStatuses } = getState().annotation;

    if (!image) {
      return;
    }

    let updatedSelectedSequenceInfo: SequenceInfo;
    let updatedImagesCache: CachedImage[];
    let updatedSequenceStatuses: SequenceStatusInfo[];

    if (!(selectedSequenceInfo && selectedSequenceInfo.images)) {
      dispatch(
        setDataAfterImageQCAssessment({
          assessmentData
        })
      );
      return;
    }

    updatedSelectedSequenceInfo = {
      ...selectedSequenceInfo,
      images: selectedSequenceInfo.images.map(x => {
        if (x.filename === image!.name) {
          return {
            ...x,
            haveQC: assessmentData.haveQC
          };
        }
        return x;
      })
    };

    updatedImagesCache = imagesCache.map(x => {
      if (x.image.name === image!.name) {
        return {
          ...x,
          image: {
            ...x.image,
            dataQCAssessment: assessmentData
          }
        };
      }

      return x;
    });

    updatedSequenceStatuses = sequenceStatuses.map(x => {
      if (x.blade === selectedSequenceInfo.bladePosition && x.bladeSide === selectedSequenceInfo.bladeSide) {
        let newHaveQC = false;
        if (selectedSequenceInfo.images.some(x => x.haveQC)) {
          newHaveQC = true;
        }

        return {
          ...x,
          haveQC: newHaveQC
        };
      }
      return x;
    });

    dispatch(
      setDataAfterImageQCAssessment({
        assessmentData,
        updatedSelectedSequenceInfo,
        updatedImagesCache,
        updatedSequenceStatuses
      })
    );
  };
